import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { UsersProvider } from './context/UsersContext';
import { NotificationProvider } from './context/NotificationContext';
import Sidebar from './components/Sidebar';
import Notification from './components/Notification';
import AdminSidebar from './components/AdminSidebar';
import ProfileSidebar from './components/ProfileSidebar';
import ProtectedRoute from './components/ProtectedRoute';
import PermissionRoute from './components/PermissionRoute';
import { PERMISSIONS } from './hooks/usePermissions';
import Login from './pages/Login';
import ChecksDashboard from './pages/ChecksDashboard';
import DrlDashboard from './pages/DrlDashboard';
import Checks from './pages/Checks';
import CheckDetails from './pages/CheckDetails';
import CheckUpload from './pages/CheckUpload';
import BatchValidation from './pages/BatchValidation';
import BatchUpload from './pages/BatchUpload';
import CheckUpdate from './pages/CheckUpdate';
import ClarificationValidation from './pages/ClarificationValidation';
import CreateCheck from './pages/CreateCheck';
import Clarifications from './pages/Clarifications';
import DrlClarifications from './pages/DrlClarifications';
import Unknown from './pages/Unknown';
import DITDRLPayments from './pages/DITDRLPayments';
import DitDrl from './pages/DitDrl';
import DitDrlDetails from './pages/DitDrlDetails';
import CreateDrl from './pages/CreateDrl';
import Users from './pages/admin/Users';
import MasterData from './pages/admin/MasterData';
import Practices from './pages/admin/Practices';
import Roles from './pages/admin/Roles';
import RoleForm from './pages/admin/RoleForm';
import CheckTrash from './pages/admin/CheckTrash';
import CheckTrashDetails from './pages/admin/CheckTrashDetails';
import Profile from './pages/Profile';
import SsoCallback from './pages/SsoCallback';
import './App.css';

const ReconValidation = lazy(() => import('./pages/ReconValidation'));

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();
  const [showAdminSubmenu, setShowAdminSubmenu] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [showAdminSidebar, setShowAdminSidebar] = useState(false);

  const isAdminRoute = location.pathname.startsWith('/admin');
  const isProfileRoute = location.pathname === '/profile';
  const isAuthPage =
    location.pathname === '/login' ||
    location.pathname === '/auth/callback' ||
    location.pathname === '/api/v1/auth/microsoft/callback';

  // Handle admin sidebar state
  useEffect(() => {
    if (isAdminRoute) {
      setShowAdminSidebar(true);
      setShowAdminSubmenu(true);
    } else {
      setShowAdminSidebar(false);
    }
  }, [isAdminRoute]);

  // Show login page immediately — isAuthenticated is known synchronously from
  // localStorage on the very first render, so no loading flash occurs.
  if (isAuthPage) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<SsoCallback />} />
        <Route path="/api/v1/auth/microsoft/callback" element={<SsoCallback />} />
      </Routes>
    );
  }

  // If still loading (e.g. during logout), show a minimal spinner.
  // In normal operation this is never true because auth state is initialized
  // synchronously in AuthContext.
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: '#f9fafb'
      }}>
        <div style={{ fontSize: '16px', color: '#6b7280' }}>Loading...</div>
      </div>
    );
  }

  // Not authenticated — redirect to login immediately during render, before
  // the full app tree (sidebar, routes, PermissionRoute components) is ever
  // mounted.  This eliminates the brief "flash of app content" that was
  // triggering cascading <Navigate> effects and causing the login card to
  // appear to refresh multiple times.
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const handleAdminClick = () => {
    if (!isAdminRoute) {
      // Navigate to users page by default when admin portal is clicked
      navigate('/admin/users');
      setShowAdminSidebar(true);
    } else {
      // If already on admin route, just toggle
      setShowAdminSidebar(!showAdminSidebar);
    }
    setShowAdminSubmenu(true);
  };

  const handleToggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const handleCloseAdminSidebar = () => {
    setShowAdminSidebar(false);
    // Don't navigate - keep user on current page
  };

  const showSecondarySidebar = showAdminSidebar || isProfileRoute;

  return (
    <div className={`app ${isSidebarCollapsed ? 'sidebar-collapsed' : ''} ${showSecondarySidebar ? 'admin-sidebar-open' : ''}`}>
      <Sidebar 
        showAdminSubmenu={showAdminSubmenu} 
        onAdminClick={handleAdminClick}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={handleToggleSidebar}
      />
      {showAdminSidebar && (
        <AdminSidebar 
          onClose={handleCloseAdminSidebar}
          isMainSidebarCollapsed={isSidebarCollapsed}
        />
      )}
      {isProfileRoute && (
        <ProfileSidebar
          isMainSidebarCollapsed={isSidebarCollapsed}
          onClose={() => navigate(-1)}
        />
      )}
      <div className={`main-content ${isSidebarCollapsed ? 'sidebar-collapsed' : ''} ${showSecondarySidebar ? 'admin-sidebar-open' : ''}`}>
        <Routes>
          {/* Dashboard - Redirect to Checks Dashboard by default */}
          <Route path="/" element={<Navigate to="/dashboard/checks" replace />} />
          
          {/* Dashboard Routes */}
          <Route path="/dashboard/checks" element={
            <PermissionRoute permission={PERMISSIONS.CHECK_DASHBOARD_VIEW}>
              <ChecksDashboard />
            </PermissionRoute>
          } />
          <Route path="/dashboard/drl" element={
            <PermissionRoute permission={PERMISSIONS.DIT_DRL_DASHBOARD_VIEW}>
              <DrlDashboard />
            </PermissionRoute>
          } />
          
          {/* Checks - Requires CHECK_DASHBOARD_VIEW permission */}
          <Route path="/checks" element={
            <PermissionRoute permission={PERMISSIONS.CHECK_DASHBOARD_VIEW}>
              <Checks />
            </PermissionRoute>
          } />
          <Route path="/checks/:id" element={
            <PermissionRoute permission={PERMISSIONS.CHECK_GET}>
              <CheckDetails />
            </PermissionRoute>
          } />
          
          {/* Check Upload - Requires BULK_IMPORT_JOB_CREATE permission */}
          <Route path="/check-upload" element={
            <PermissionRoute permission={PERMISSIONS.BULK_IMPORT_JOB_CREATE}>
              <CheckUpload />
            </PermissionRoute>
          } />

          {/* Batch Validation - Requires BULK_IMPORT_JOB_CREATE permission */}
          <Route path="/batch-validation" element={
            <PermissionRoute permission={PERMISSIONS.BULK_IMPORT_JOB_CREATE}>
              <BatchValidation />
            </PermissionRoute>
          } />

          {/* Batch Upload - Requires BULK_IMPORT_JOB_CREATE permission */}
          <Route path="/batch-upload" element={
            <PermissionRoute permission={PERMISSIONS.BULK_IMPORT_JOB_CREATE}>
              <BatchUpload />
            </PermissionRoute>
          } />

          {/* Check Update - Requires BULK_IMPORT_JOB_CREATE permission */}
          <Route path="/check-update" element={
            <PermissionRoute permission={PERMISSIONS.BULK_IMPORT_JOB_CREATE}>
              <CheckUpdate />
            </PermissionRoute>
          } />

          {/* Recon Validation - Requires BULK_IMPORT_JOB_CREATE permission */}
          <Route path="/recon-validation" element={
            <PermissionRoute permission={PERMISSIONS.BULK_IMPORT_JOB_CREATE}>
              <Suspense fallback={<div className="main-content" style={{ padding: '24px', color: '#6b7280' }}>Loading…</div>}>
                <ReconValidation />
              </Suspense>
            </PermissionRoute>
          } />

          {/* Clarification Validation - Requires CLARIFICATION_VALIDATION_VIEW permission */}
          <Route path="/clarification-validation" element={
            <PermissionRoute permission={PERMISSIONS.CLARIFICATION_VALIDATION_VIEW}>
              <ClarificationValidation />
            </PermissionRoute>
          } />
          
          {/* Create Check - Requires CHECK_CREATE permission */}
          <Route path="/checks/new" element={
            <PermissionRoute permission={PERMISSIONS.CHECK_CREATE}>
              <CreateCheck />
            </PermissionRoute>
          } />
          
          {/* Clarifications - Redirect to Check Clarifications by default */}
          <Route path="/clarifications" element={<Navigate to="/clarifications/checks" replace />} />
          <Route path="/clarifications/checks" element={
            <PermissionRoute permission={PERMISSIONS.CHECK_CLARIFICATION_DASHBOARD_VIEW}>
              <Clarifications />
            </PermissionRoute>
          } />
          <Route path="/clarifications/drl" element={
            <PermissionRoute permission={PERMISSIONS.DIT_DRL_CLARIFICATION_DASHBOARD_VIEW}>
              <DrlClarifications />
            </PermissionRoute>
          } />
          
          {/* Unknown - Requires CHECK_DASHBOARD_VIEW permission */}
          <Route path="/unknown" element={
            <PermissionRoute permission={PERMISSIONS.CHECK_DASHBOARD_VIEW}>
              <Unknown />
            </PermissionRoute>
          } />
          
          {/* DIT/DRL Payments - Note: This seems to be a legacy route, keeping for compatibility */}
          <Route path="/dit-drl-payments" element={
            <PermissionRoute permission={PERMISSIONS.DIT_DRL_DASHBOARD_VIEW}>
              <DITDRLPayments />
            </PermissionRoute>
          } />
          
          {/* DIT/DRL - Requires DIT_DRL_DASHBOARD_VIEW permission */}
          <Route path="/dit-drl" element={
            <PermissionRoute permission={PERMISSIONS.DIT_DRL_DASHBOARD_VIEW}>
              <DitDrl />
            </PermissionRoute>
          } />
          <Route path="/dit-drl/new" element={
            <PermissionRoute permission={PERMISSIONS.DIT_DRL_CREATE}>
              <CreateDrl />
            </PermissionRoute>
          } />
          <Route path="/dit-drl/:id" element={
            <PermissionRoute permission={PERMISSIONS.DIT_DRL_GET}>
              <DitDrlDetails />
            </PermissionRoute>
          } />
          
          {/* DRL Dashboard Routes */}
          <Route path="/drl-dashboard" element={
            <PermissionRoute permission={PERMISSIONS.DIT_DRL_DASHBOARD_VIEW}>
              <DrlDashboard />
            </PermissionRoute>
          } />
          
          {/* Create DRL - Requires DIT_DRL_CREATE permission */}
          <Route path="/drl/new" element={
            <PermissionRoute permission={PERMISSIONS.DIT_DRL_CREATE}>
              <CreateDrl />
            </PermissionRoute>
          } />
          <Route path="/drl/:id" element={
            <PermissionRoute permission={PERMISSIONS.DIT_DRL_GET}>
              <DitDrlDetails />
            </PermissionRoute>
          } />
          
          {/* Profile - Available to all authenticated users */}
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          
          {/* Admin Portal Routes */}
          <Route path="/admin" element={
            <PermissionRoute permission={[
              PERMISSIONS.ADMIN_PORTAL_DASHBOARD_VIEW,
              PERMISSIONS.USER_LIST,
              PERMISSIONS.ROLE_LIST,
              PERMISSIONS.PRACTICE_LIST
            ]} fallbackPath="/">
              <Navigate to="/admin/users" replace />
            </PermissionRoute>
          } />
          
          {/* Users Management - Requires USER_LIST permission */}
          <Route path="/admin/users" element={
            <PermissionRoute permission={PERMISSIONS.USER_LIST}>
              <Users />
            </PermissionRoute>
          } />
          
          {/* Practices Management - Requires PRACTICE_LIST permission */}
          <Route path="/admin/practices" element={
            <PermissionRoute permission={PERMISSIONS.PRACTICE_LIST}>
              <Practices />
            </PermissionRoute>
          } />
          
          {/* Roles Management - Requires ROLE_LIST permission */}
          <Route path="/admin/roles" element={
            <PermissionRoute permission={PERMISSIONS.ROLE_LIST}>
              <Roles />
            </PermissionRoute>
          } />
          
          {/* Master Data - Requires ADMIN_PORTAL_DASHBOARD_VIEW permission */}
          <Route path="/admin/master-data" element={
            <PermissionRoute permission={PERMISSIONS.ADMIN_PORTAL_DASHBOARD_VIEW}>
              <MasterData />
            </PermissionRoute>
          } />
          
          {/* Role Form - Create/Edit - Requires ROLE_CREATE or ROLE_UPDATE permission */}
          <Route path="/admin/roles/new" element={
            <PermissionRoute permission={PERMISSIONS.ROLE_CREATE}>
              <RoleForm />
            </PermissionRoute>
          } />
          <Route path="/admin/roles/:id/edit" element={
            <PermissionRoute permission={PERMISSIONS.ROLE_UPDATE}>
              <RoleForm />
            </PermissionRoute>
          } />

          {/* Check Trash - Requires ADMIN_PORTAL_DASHBOARD_VIEW permission */}
          <Route path="/admin/check-trash" element={
            <PermissionRoute permission={PERMISSIONS.ADMIN_PORTAL_DASHBOARD_VIEW}>
              <CheckTrash />
            </PermissionRoute>
          } />
          <Route path="/admin/check-trash/:id" element={
            <PermissionRoute permission={PERMISSIONS.ADMIN_PORTAL_DASHBOARD_VIEW}>
              <CheckTrashDetails />
            </PermissionRoute>
          } />
        </Routes>
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <UsersProvider>
        <NotificationProvider>
          <Router>
            <AppContent />
            <Notification />
          </Router>
        </NotificationProvider>
      </UsersProvider>
    </AuthProvider>
  );
}

export default App;
